import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AbstractService } from './abstract.service';
import {
  IAuthRoleAttributes,
  ICreateAuthRoleDto,
  IUpdateAuthRoleDto,
  IAuthRoleResult,
  IAuthRoleListResult,
} from '../shared/interfaces/auth-role.interface';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthRoleService extends AbstractService<
  IAuthRoleAttributes,
  ICreateAuthRoleDto,
  IUpdateAuthRoleDto
> {
  constructor(http: HttpClient) {
    super(environment.API_AVE_AUTHORIZATION_URL, 'auth_role', http);
  }

  /**
   * Get roles with filter query (GET method)
   * @param filter Filter object to convert to query string
   * @returns Observable with filtered roles
   */
  filter(filter?: Record<string, any>): Observable<IAuthRoleListResult> {
    const apiUrl = `${environment.API_AVE_AUTHORIZATION_URL}api/auth_rol/filter`;
    
    // Convert filter object to query string
    let params = new HttpParams();
    if (filter) {
      params = params.set('filter', JSON.stringify(filter));
    }
    
    return this.http.get<IAuthRoleListResult>(apiUrl, { params });
  }

  /**
   * Get role by ID
   * @param id Role MongoDB _id
   * @returns Observable with role data
   */
  findById(id: string): Observable<IAuthRoleResult> {
    const apiUrl = `${environment.API_AVE_AUTHORIZATION_URL}api/auth_rol/byId/${id}`;
    return this.http.get<IAuthRoleResult>(apiUrl);
  }

  /**
   * Create a new RBAC role
   * @param data Role data (code and name)
   * @returns Observable with created role
   */
  override create(data: ICreateAuthRoleDto): Observable<IAuthRoleResult> {
    const apiUrl = `${environment.API_AVE_AUTHORIZATION_URL}api/auth_role`;
    return this.http.post<IAuthRoleResult>(apiUrl, data);
  }

  /**
   * Update an existing RBAC role
   * @param id Role MongoDB _id
   * @param data Role data to update (name and/or statusId)
   * @returns Observable with updated role
   */
  override update(id: string, data: IUpdateAuthRoleDto): Observable<IAuthRoleResult> {
    const apiUrl = `${environment.API_AVE_AUTHORIZATION_URL}api/auth_role/${id}`;
    return this.http.patch<IAuthRoleResult>(apiUrl, data);
  }

  /**
   * Delete a RBAC role
   * @param id Role MongoDB _id
   * @returns Observable with deleted role
   */
  delete(id: string): Observable<IAuthRoleResult> {
    const apiUrl = `${environment.API_AVE_AUTHORIZATION_URL}api/auth_role/${id}`;
    return this.http.delete<IAuthRoleResult>(apiUrl);
  }
}
